// Themes studio writes: create, patch, duplicate, delete, apply. Ordinary ops through `mutate`,
// so they go to Dexie first, sync, and undo. The Prime tools of a later task wrap these.
import { db, mutate } from '@/runtime'
import { memberGrant, settingsIdNow } from '@/state/access'
import { patchSettings } from '@/lib/settingsWrite'
import { useDevice } from '@/state/appearance'
import { withStudio, type PersonLook } from '@/lib/look'
import { OWNER_AUTHOR, type ProfileSpec, type ThemeSpec } from '@/kinds/themeprofile/schema'
import type { ThemeProfileResource } from '@/kinds/themeprofile/apply'
import {
  duplicateSpec,
  newProfileSpec,
  profileLabels,
  profilePatch,
  type NewProfile,
  type ProfilePatch,
} from './profiles'

/** The author key this device writes under. */
export const myAuthorKey = (): string => {
  const g = memberGrant()
  return g ? `grant:${g}` : OWNER_AUTHOR
}

export const createProfile = (p: NewProfile, ws?: string) =>
  mutate.create('ThemeProfile', newProfileSpec(p), profileLabels(ws, myAuthorKey()))

export const patchProfile = async (r: ThemeProfileResource, change: ProfilePatch) => {
  const spec = profilePatch(r.spec, change)
  if (Object.keys(spec).length) await mutate.patch(r.id, { spec })
}

/** Replace a profile's fields as the editor holds them (the whole theme, so a cleared section is gone). */
export const saveProfile = async (
  r: ThemeProfileResource,
  next: { name: string; emoji: string; visibility: ProfileSpec['visibility']; theme: ThemeSpec },
) => {
  const spec: Partial<ProfileSpec> = {}
  if (next.name.trim() !== r.spec.name) spec.name = next.name.trim()
  if (next.emoji !== r.spec.emoji) spec.emoji = next.emoji
  if (JSON.stringify(next.theme) !== JSON.stringify(r.spec.theme)) spec.theme = next.theme
  const visibilityChanged = next.visibility !== r.spec.visibility
  if (visibilityChanged) spec.visibility = next.visibility
  if (!Object.keys(spec).length) return
  // the server keeps the `visibility` label equal to the spec's: one op changes both
  await mutate.patch(r.id, {
    spec,
    ...(visibilityChanged ? { labels: { visibility: next.visibility } } : {}),
  })
}

export const duplicateProfile = (
  src: ThemeProfileResource,
  copier: { authorName?: string; name?: string },
  ws?: string,
) =>
  mutate.create('ThemeProfile', duplicateSpec(src, copier), profileLabels(ws ?? src.labels.ws, myAuthorKey()))

export const deleteProfile = (id: string) => mutate.remove(id)

const personLook = async (): Promise<PersonLook | undefined> =>
  (await db.resources.get(settingsIdNow()))?.spec?.person_look as PersonLook | undefined

/** Wear a theme: a snapshot into this person's own look; `profile` is where it came from. */
export const applyTheme = async (spec: ThemeSpec, profile?: string) => {
  await patchSettings({ person_look: withStudio(await personLook(), { profile, spec }) })
  // wearing it is meant to be seen here: this device stops overriding the theme
  useDevice.getState().set({ theme: null })
}

export const applyProfile = (r: ThemeProfileResource) => applyTheme(r.spec.theme, r.id)

/** Stop wearing a studio theme: the look is whatever it was before. */
export const clearApplied = async () => patchSettings({ person_look: withStudio(await personLook(), null) })
